import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/env';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => sessionStorage.getItem('authToken'));
  const [user, setUser] = useState(null);
  // isLoading stays true until we've verified the session (or confirmed no token)
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userData');
    localStorage.removeItem('cctns_user');
  };

  // Automatically attach Bearer token to all axios requests if available
  useEffect(() => {
    const storedToken = sessionStorage.getItem('authToken');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      verifySession(storedToken);
    } else {
      // No token at all — immediately mark loading as done
      setIsLoading(false);
    }
  }, []);

  const verifySession = async (tokenToVerify) => {
    try {
      const res = await axios.get(`${API_BASE}/login`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` },
      });
      if (res.data && res.data.success && res.data.user) {
        setToken(tokenToVerify);
        setUser(res.data.user);
      } else {
        logout();
      }
    } catch (e) {
      console.warn('Session verification notice:', e.message);
      if (e.response?.status === 401 || e.response?.status === 404) {
        logout();
      } else {
        // Network/server error — try restoring user from local storage
        try {
          const sessionUserData = sessionStorage.getItem('userData');
          if (sessionUserData) {
            const parsed = JSON.parse(sessionUserData);
            if (parsed.userData) {
              setToken(tokenToVerify);
              setUser(parsed.userData);
              return;
            }
          }
          const savedUser = localStorage.getItem('cctns_user');
          if (savedUser) {
            setToken(tokenToVerify);
            setUser(JSON.parse(savedUser));
          }
        } catch { logout(); }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, pass) => {
    try {
      const response = await axios.post(`${API_BASE}/login`, { username, pass });
      if (response.data && response.data.success) {
        const receivedToken = response.data.token;
        const userData = response.data.user || { username, usertype: response.data.user?.usertype || 1 };
        
        sessionStorage.setItem('authToken', receivedToken);
        sessionStorage.setItem('userData', JSON.stringify({ isLoggedIn: true, userData }));
        localStorage.setItem('cctns_user', JSON.stringify(userData));
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${receivedToken}`;
        setToken(receivedToken);
        setUser(userData);
        return { success: true, message: 'Login successful' };
      } else {
        return { success: false, message: response.data?.message || 'Wrong username/password combination!' };
      }
    } catch (err) {
      console.error('Backend login error:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Login failed. Please check connection and credentials.',
      };
    }
  };

  const isAuthenticated = Boolean(user && token);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isLoading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
